import {
	addMilliseconds,
	addMinutes,
	differenceInMilliseconds,
	eachMinuteOfInterval,
	endOfDay,
	format,
	isAfter,
	isBefore,
	isSameDay,
	parse,
	roundToNearestMinutes,
	startOfDay,
} from "date-fns";

import { CalendarErrorType } from "./types";

import type { SearchServiceSort } from "../../../api/hooks/defs/search-service";

import type { GetMeetingsSearchParams } from "../../../api/services/defs/internal-api";

import { config } from "./config";
import type { WorkspaceDetailsFilters } from "./schema";
import { MeetingFormSchema } from "./schema";

/**
 * Calculates default start and end times for a new meeting
 *
 * The start time is rounded to the next 15-minute interval, ensuring it's not in the past.
 * If the current time is already on a 15-minute interval, it will use the next interval.
 * The end time is set to 60 minutes after the calculated start time.
 *
 * @param {Date} [date=new Date()] - Base date to calculate from, defaults to current time
 * @returns Object containing default start and end times
 */
export const getDefaultMeetingTimes = (date: Date = new Date()) => {
	const now = new Date();

	// If provided date is in the past, use current time as base
	const baseDate = date < now ? now : date;

	// Round to nearest 15 minutes
	let startDateTime = roundToNearestMinutes(baseDate, { nearestTo: 15 });

	// If the rounded time is in the past or equal to now, add 15 minutes to get the next interval
	if (startDateTime <= now) {
		startDateTime = addMinutes(startDateTime, 15);
	}

	// Add 60 minutes to start time for end time
	const endDateTime = addMinutes(startDateTime, 60);

	return { startDateTime, endDateTime };
};

/**
 * Generates an array of time options in 15-minute increments for a full day
 *
 * Creates time options from start of day to end of day for the selected date.
 * Each option contains a 12-hour formatted label (e.g. "9:30am") and 24-hour formatted value (e.g. "09:30").
 *
 * @param {Date} selectedDate - The date to generate time options for
 * @returns Array of time options with label and value properties
 */
export const generateTimeOptions = (selectedDate: Date = new Date()) => {
	const start = startOfDay(selectedDate);
	const end = endOfDay(selectedDate);
	const times = eachMinuteOfInterval({ start, end }, { step: 15 });

	return times.map((time) => ({
		// Format label as 12-hour time (e.g., "9:30am")
		label: format(time, "h:mma").toLowerCase(),
		// Format value as 24-hour time (e.g., "09:30")
		value: format(time, "HH:mm"),
	}));
};

/**
 * Convert the filters, sort, and page number from the UI into the search service API params.
 */
export const convertFiltersToSearchParams = (
	filters: WorkspaceDetailsFilters,
	sort: SearchServiceSort,
	pageNumber: number,
	workspaceId: string,
	userId: string
) => {
	const searchParams: GetMeetingsSearchParams = {
		meetingWorkspaceId: workspaceId,
		limit: config.pageSize,
		skip: (pageNumber - 1) * config.pageSize,
		sort: { name: sort.name as any, direction: sort.direction },
	};

	if (filters.status && filters.status.length > 0) {
		searchParams.status = filters.status;
	}

	// Private notes filter logic - add the userId for filtering user-specific meetings
	if (filters.privateNotes && filters.privateNotes !== "any") {
		searchParams.participantId = userId;
		searchParams.privateNotesFilter = filters.privateNotes;
	}

	// Date range filters - apply start-of-day/end-of-day logic
	if (filters.meetingDateStart) {
		searchParams.dateRangeStart = startOfDay(filters.meetingDateStart);
	}

	if (filters.meetingDateEnd) {
		searchParams.dateRangeEnd = endOfDay(filters.meetingDateEnd);
	}

	return searchParams;
};

/**
 * Calculates the new end date and time based on a new start date/time,
 * preserving the original duration between the initial start and end date/times.
 * If the original duration is non-positive, defaults to a 60-minute duration.
 */
const calculateNewEndDateTime = (
	originalStartDate: Date,
	originalStartTime: string,
	originalEndDate: Date,
	originalEndTime: string,
	newStartDate: Date,
	newStartTime: string
): { newEndDate: Date; newEndTime: string } => {
	const originalStartDateTime = parse(originalStartTime, "HH:mm", originalStartDate);
	const originalEndDateTime = parse(originalEndTime, "HH:mm", originalEndDate);
	let durationMs = differenceInMilliseconds(originalEndDateTime, originalStartDateTime);

	// Default to 60 minutes if duration is not positive
	if (durationMs <= 0) {
		durationMs = 60 * 60 * 1000; // 60 minutes in milliseconds
	}

	const newStartDateTime = parse(newStartTime, "HH:mm", newStartDate);
	const newEndDateTime = addMilliseconds(newStartDateTime, durationMs);

	return {
		newEndDate: startOfDay(newEndDateTime),
		newEndTime: format(newEndDateTime, "HH:mm"),
	};
};

/**
 * Handles changes to the start date, adjusting the end date/time if necessary
 * to maintain the original duration or prevent invalid states.
 */
export const handleStartDateChange = (
	val: Date | null | undefined,
	formValues: MeetingFormSchema,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	// If date is cleared, set to undefined to trigger validation
	if (!val) {
		onFormChange({ startDate: undefined });
		return;
	}

	const newStartDate = startOfDay(val);

	// Always update the start date
	let changes: Partial<MeetingFormSchema> = { startDate: newStartDate };

	// If the new start date is after the current end date,
	// or if they are the same day but the start time is on or after the end time,
	// we need to adjust the end date and time.
	if (
		isAfter(newStartDate, formValues.endDate) ||
		(isSameDay(newStartDate, formValues.endDate) && formValues.startTime >= formValues.endTime)
	) {
		const { newEndDate, newEndTime } = calculateNewEndDateTime(
			formValues.startDate,
			formValues.startTime,
			formValues.endDate,
			formValues.endTime,
			newStartDate, // Use the new start date for calculation
			formValues.startTime // Use the existing start time
		);
		changes = { ...changes, endDate: newEndDate, endTime: newEndTime };
	}

	onFormChange(changes);
};

/**
 * Handles changes to the end date, adjusting the start date/time if necessary
 * to prevent invalid states. Adjusts end time if it becomes invalid relative to start time
 * on the same day.
 */
export const handleEndDateChange = (
	val: Date | null | undefined,
	formValues: MeetingFormSchema,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	// If date is cleared, set to undefined to trigger validation
	if (!val) {
		onFormChange({ endDate: undefined });
		return;
	}

	const newEndDate = startOfDay(val);
	let changes: Partial<MeetingFormSchema> = { endDate: newEndDate };

	// If the new end date is before the start date, adjust the start date.
	if (isBefore(newEndDate, formValues.startDate)) {
		changes = { ...changes, startDate: newEndDate };
		// Re-evaluate if end time needs adjustment based on the *potentially* new start date (`newEndDate`)
		if (formValues.endTime <= formValues.startTime) {
			const { newEndDate: adjustedEndDate, newEndTime } = calculateNewEndDateTime(
				newEndDate, // Use new end date as the temporary start date for calc
				formValues.startTime,
				newEndDate, // Use new end date for original end date context
				formValues.endTime,
				newEndDate, // Base new end time calculation on this date
				formValues.startTime
			);
			// Only update endDate if the calculation specifically requires it (e.g., crosses midnight)
			if (!isSameDay(adjustedEndDate, newEndDate)) {
				changes = { ...changes, endDate: adjustedEndDate, endTime: newEndTime };
			} else {
				changes = { ...changes, endTime: newEndTime };
			}
		}
	}
	// If the new end date is the same day as the start date,
	// check if the end time needs adjustment relative to the start time.
	else if (isSameDay(formValues.startDate, newEndDate) && formValues.endTime <= formValues.startTime) {
		const { newEndDate: adjustedEndDate, newEndTime } = calculateNewEndDateTime(
			formValues.startDate,
			formValues.startTime,
			newEndDate, // Use the new end date for calculation context
			formValues.endTime,
			formValues.startDate, // Base new end time calculation on start date
			formValues.startTime
		);
		// Update endDate only if necessary (duration crosses midnight)
		if (!isSameDay(adjustedEndDate, newEndDate)) {
			changes = { ...changes, endDate: adjustedEndDate, endTime: newEndTime };
		} else {
			changes = { ...changes, endTime: newEndTime };
		}
	}

	onFormChange(changes);
};

/**
 * Handles changes to the start time, adjusting the end date/time if necessary
 * to maintain the original duration when the start time surpasses the end time on the same day.
 */
export const handleStartTimeChange = (
	option: { value: string },
	formValues: MeetingFormSchema,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	const newStartTime = option.value;
	let changes: Partial<MeetingFormSchema> = { startTime: newStartTime };

	// If start and end are on the same day and the new start time is on or after the end time,
	// adjust the end date/time preserving the original duration.
	if (isSameDay(formValues.startDate, formValues.endDate) && newStartTime >= formValues.endTime) {
		const { newEndDate, newEndTime } = calculateNewEndDateTime(
			formValues.startDate,
			formValues.startTime, // Original start time for duration calc
			formValues.endDate,
			formValues.endTime,
			formValues.startDate, // Base new end calculation on start date
			newStartTime // Use the new start time
		);
		changes = { ...changes, endDate: newEndDate, endTime: newEndTime };
	}

	onFormChange(changes);
};

/**
 * Check if an error is a calendar-related error that needs a modal
 * Only returns true for specific cases that require user follow-up actions
 */
export const isCalendarErrorRequiringModal = (
	error: any
): { requiresModal: boolean; errorType?: CalendarErrorType } => {
	// Check if it's a 422 error with calendar error information
	if (error?.response?.status !== 422) {
		return { requiresModal: false };
	}

	const calendarErrorType = error?.data?.meta?.calendarErrorType;

	// Only show modal for specific error types that require user decisions
	if (calendarErrorType === "CREATION_FAILED") {
		return { requiresModal: true, errorType: CalendarErrorType.CREATE_FAILED };
	}

	if (calendarErrorType === "UPDATE_FAILED") {
		return { requiresModal: true, errorType: CalendarErrorType.UPDATE_FAILED };
	}

	return { requiresModal: false };
};

/**
 * Get appropriate error toast message based on operation data
 * Provides status-specific and operation-specific error messages
 */
export const getErrorToastMessage = (operationData: {
	status?: string;
	addToCalendar?: boolean;
	removeFromCalendar?: boolean;
}): string => {
	// Provide status-specific error messages
	if (operationData.status) {
		switch (operationData.status) {
			case "archived":
				return "Failed to Archive Meeting";
			case "canceled":
				return "Failed to Cancel Meeting";
			case "completed":
				return "Failed to Complete Meeting";
			case "active":
				return "Failed to Reopen Meeting";
		}
	}

	// Check for specific calendar operations
	if (operationData.addToCalendar) {
		return "Failed to Add Meeting to Calendar";
	}

	if (operationData.removeFromCalendar) {
		return "Failed to Remove Meeting from Calendar";
	}

	// Default error messages
	return "Failed to Update Meeting";
};

/**
 * Get appropriate success toast message based on operation and calendar involvement
 * This handles the complex logic for meeting updates with calendar integration
 *
 * @param params - The parameters for generating the success message
 * @param params.calendarOperationType - The type of calendar operation performed
 * @param params.operationData - Data about the operation (status, calendar flags)
 * @param params.isInitialCreation - Whether this is during initial meeting creation
 * @returns The appropriate success toast message
 */
export const getCalendarSuccessToastMessage = (params: {
	calendarOperationType: string | undefined;
	operationData: {
		addToCalendar?: boolean;
		removeFromCalendar?: boolean;
		status?: string;
	};
	isInitialCreation?: boolean;
}): string => {
	const { calendarOperationType, operationData, isInitialCreation = false } = params;

	// Handle status changes first
	if (operationData.status) {
		switch (operationData.status) {
			case "archived":
				return "Meeting Archived";
			case "completed":
				return "Meeting Completed";
			case "canceled":
				// Check if calendar event was also removed
				if (operationData.removeFromCalendar) {
					return "Meeting Canceled and Removed from Calendar";
				} else {
					return "Meeting Canceled";
				}
			default:
				return "Meeting Updated";
		}
	}

	// Handle calendar-specific operations
	if (operationData.addToCalendar) {
		// Only show "Meeting Created and Added to Calendar" during initial meeting creation
		if (calendarOperationType === "created" && isInitialCreation) {
			return "Meeting Created and Added to Calendar";
		} else {
			return "Meeting Added to Calendar";
		}
	}

	// Handle calendar removal
	if (operationData.removeFromCalendar) {
		return "Meeting Removed from Calendar";
	}

	// Handle calendar update
	if (calendarOperationType === "updated") {
		return "Meeting Updated and Synced With Calendar";
	}

	// Handle calendar event not found (handled as error toast separately)
	if (calendarOperationType === "calendar_event_not_found") {
		return ""; // This will be handled as an error toast separately
	}

	// Default success messages based on operation type
	if (calendarOperationType === "created") {
		return "Meeting Created";
	}

	// Default success messages
	return "Meeting Updated";
};
